// Ambient sky — the helpers behind AmbientBackdrop
// (DESIGN-WL-AMBIENT-SKIN-BACKDROP-1, HOME_AMBIENT_SKIN_SPEC §1–§2).
//
// FRANK: every export is invoked through `el.call(...)` and is SELF-CONTAINED.
// Frank stringifies each export on its own, so no export reaches another
// top-level binding bare — it goes through `this.call(...)` (the element) or
// carries its own copy of a literal. Same FA204 rule as
// shared/functions/workspaceSettings.js; __tests__/ambientBackdrop.test.js
// locks it.
//
// THE SKIN is never decided or read here. The shell stamps <html data-skin>
// from functions/workspaceSkin.js `resolveWorkspaceSkin` (the one skin rule,
// also read by the admin Appearance card; NO attribute means solid), and the
// shell's injected sheet shows the layer for the ambient skin only (app.js,
// docs/patches/ambient-mount.app.js.patch) — so the sky and the stamp can
// never disagree. This file adds only the SKY's own off switches (§2).

// → { enabled, sky, reason }. env: { reduceTransparency, stageOverride,
// pending, resolvedSky, userSky }. The sky follows the server's contract (core
// 9193ff2c, workspace.resolvedAppearance): the record's own valid pick, then
// the server's resolved sky, then the local default — the precedence
// functions/workspaceSkin.js uses for the skin. OVER all three, the PERSON's
// own sky (`userSky`, user_preferences.homeLook.sky through the Personalise
// panel — functions/homeLook.js; DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1): an
// explicit choice wins over the workspace's, 'none' included, and `null`
// follows the workspace. The first matching off switch wins: pending > the
// person's none > the workspace's none > reduce-transparency > stage-override
// (the skin gate is the stamp, in CSS). `pending` = the record is in flight: a
// render site never paints a default over a workspace that may be configured
// otherwise. The catalogue is the set of masks in files/ambient; its first
// entry is the spec default (`clouds`).
export const resolveSky = function resolveSky(settings, env) {
  const catalogue = ['clouds', 'dunes', 'hills', 'lake', 'window', 'mountains']
  const e = env && typeof env === 'object' ? env : {}
  const bag = settings && typeof settings === 'object' ? settings.appearance : null
  const appearance = bag && typeof bag === 'object' ? bag : {}
  const off = (reason) => ({ enabled: false, sky: null, reason })
  const valid = (v) => v === 'none' || catalogue.includes(v)
  const user = valid(e.userSky) ? e.userSky : null
  const picked = valid(appearance.sky) ? appearance.sky : null
  const resolved = !picked && valid(e.resolvedSky) ? e.resolvedSky : null
  if (e.pending) return off('pending')
  if (user === 'none') return off('user-none')
  if (!user && (picked || resolved) === 'none') return off('sky-none')
  if (e.reduceTransparency) return off('reduce-transparency')
  if (e.stageOverride) return off('stage-override')
  if (user) return { enabled: true, sky: user, reason: 'user' }
  if (picked) return { enabled: true, sky: picked, reason: 'record' }
  if (resolved) return { enabled: true, sky: resolved, reason: 'resolved' }
  return { enabled: true, sky: catalogue[0], reason: 'default' }
}

// Does the active workspace MODULE paint its own ground? A module desk writes
// `--stage-bg` from `chrome.stage` (workspaceProjects `_moduleChrome`: config
// first), a string or a { light, dark } pair. The record's own ground forms
// are the shared rule's (functions/workspaceSkin.js `hasStageOverride`,
// mirrored by the server) — ambientSkyState asks it through el.call; this
// helper adds only the module half, which neither the skin rule nor the
// server can see.
export const ambientModuleStage = function ambientModuleStage(workspaceProject) {
  const wp = workspaceProject && typeof workspaceProject === 'object' ? workspaceProject : null
  if (!wp) return false
  const cfg = wp.config && typeof wp.config === 'object' ? wp.config : null
  const chrome = cfg && cfg.chrome !== undefined ? cfg.chrome : wp.chrome
  const stage = chrome && typeof chrome === 'object' ? chrome.stage : null
  const filled = (v) => typeof v === 'string' && v.trim() !== ''
  if (filled(stage)) return true
  return !!(stage && typeof stage === 'object' && (filled(stage.light) || filled(stage.dark)))
}

// The files-plane key of a sky mask.
export const ambientSkyFileKey = function ambientSkyFileKey(sky) {
  return typeof sky === 'string' && sky ? 'ambient/' + sky + '.png' : null
}

// The served URL of a sky mask, through the files plane: the record shape
// every file takes (`{ content: { src } }`, as brand's *.png.js records) and
// the lookup attrs-in-props `resolveFileSource` does for an Img
// `src: '/files/<key>'` (key without the prefix first, then with it). The
// shell registers files/ambient/index.js there; Parcel resolves each mask to
// its hashed, same-origin URL. No record → null → the layer paints nothing.
export const ambientSkyUrl = function ambientSkyUrl(sky) {
  if (typeof sky !== 'string' || !sky) return null
  const key = 'ambient/' + sky + '.png'
  const files = this && this.context ? this.context.files : null
  const rec = files && typeof files === 'object' ? files[key] || files['/files/' + key] : null
  const src = rec && rec.content ? rec.content.src : null
  return typeof src === 'string' && src ? src : null
}

// The `mask-image` value. No URL → 'none': the ink must never paint without
// its mask (that would be a full-screen veil).
//
// placement 'horizon' (the layer's default — Nika 2026-09-27 "move clouds
// illustration at bottom"): the art spans the full width at its own aspect
// (1440 × 960, so 2/3 of the width tall — `mask-size: 100% auto`), anchored
// to the bottom edge, and a second layer caps it to the lower half of the
// viewport and fades it out upward, so the top band (header, greeting, App
// strip) sits on clean ground. Viewport units only, never a token inside
// calc(): an unresolved var() would invalidate the whole declaration and drop
// the mask. The boot overlay (index.html) repeats these exact numbers, so
// the sky does not move when the loader hands off to the dashboard.
//
// fadeTop (px, measured; placement 'grid') adds a second layer that fades the
// sky out at the grid top; the band is derived from the measurement itself.
export const ambientMaskImage = function ambientMaskImage(url, fadeTop, placement) {
  if (typeof url !== 'string' || !url) return 'none'
  const layer = 'url("' + url.replace(/"/g, '%22') + '")'
  if (placement === 'horizon') {
    const band = 'min(66.667vw, 50vh)'
    return (
      layer +
      ', linear-gradient(to top, black 0, black calc(' +
      band +
      ' * 0.6), transparent ' +
      band +
      ')'
    )
  }
  if (typeof fadeTop !== 'number' || !Number.isFinite(fadeTop) || fadeTop <= 0) return layer
  const edge = Math.round(fadeTop)
  const solid = Math.round(edge * 0.8)
  return (
    layer +
    ', linear-gradient(to bottom, black 0, black ' +
    solid +
    'px, transparent ' +
    edge +
    'px)'
  )
}

// The FADE alone, for the still frame that holds the drifting art
// (AmbientBackdrop): the same numbers as ambientMaskImage's second layer, so
// a drifting sky keeps exactly the horizon the still one had. 'horizon' caps
// the art to the lower band and fades it out upward; 'top' is the SAME band
// on a frame that `ambientSkyFlip` mirrors to the top edge (so on screen it
// fades out DOWNWARD); 'grid' fades at the measured grid top; anything else
// has no fade ('none').
//
// THE CLEAR EDGE ('top' with a measured `fadeTop`, px from the top of the
// screen at scroll 0 — ambientFadeTop): the band never reaches past it, so the
// greeting's summary (the red overdue count measured 3.33:1 dark / 3.48:1
// light over one full ink dot; no red token of the design system reaches
// 4.5:1 there) sits on clean ground. The same horizon numbers, the band capped
// by the edge.
//
// THE TOP BAND IS TALLER (Nika 2026-10-02: "make top clouds a bit more in
// height"): its solid cloud mass runs to 75 % of the band (the bottom band
// keeps 60 %) and its cap is 60vh (the bottom band keeps 50vh). Measured on
// dev 2026-10-02 the clear edge binds at both 1440 x 900 and 390 x 844 (the
// summary's top at 140 / 139 px), so the band still ends exactly there and the
// summary keeps its clean ground; the solid part reaches 105 px instead of 84.
// `wsSkyAway` (app.js) slides the frame over the same 60vh cap.
export const ambientFadeMask = function ambientFadeMask(fadeTop, placement) {
  if (placement === 'top') {
    const edge =
      typeof fadeTop === 'number' && Number.isFinite(fadeTop) && fadeTop > 0
        ? ', ' + Math.round(fadeTop) + 'px'
        : ''
    const band = 'min(66.667vw, 60vh' + edge + ')'
    return (
      'linear-gradient(to top, black 0, black calc(' + band + ' * 0.75), transparent ' + band + ')'
    )
  }
  if (placement === 'horizon') {
    const band = 'min(66.667vw, 50vh)'
    return (
      'linear-gradient(to top, black 0, black calc(' + band + ' * 0.6), transparent ' + band + ')'
    )
  }
  if (placement !== 'grid') return 'none'
  if (typeof fadeTop !== 'number' || !Number.isFinite(fadeTop) || fadeTop <= 0) return 'none'
  const edge = Math.round(fadeTop)
  const solid = Math.round(edge * 0.8)
  return 'linear-gradient(to bottom, black 0, black ' + solid + 'px, transparent ' + edge + 'px)'
}

// THE TOP SKY (DESIGN-WL-HOME-SKY-TOP-GROUND-1, Nika 2026-10-01: "maybe we
// move moving clouds at the top side"). Placement 'top' mirrors the still
// frame vertically: the horizon band (art anchored to the frame's bottom
// edge, the fade running away from it) lands on the TOP edge of the screen,
// so the cloud mass hangs from the top and fades out downward into clean
// ground behind the greeting. One transform on the still frame: the drift
// layers keep their own translateX untouched, the mask PNGs stay as they
// are (no second asset), and the art reads the same way it always did.
// → the frame's `transform` ('none' for every other placement).
export const ambientSkyFlip = function ambientSkyFlip(placement) {
  return placement === 'top' ? 'scaleY(-1)' : 'none'
}

// THE PERSON'S LAYOUT (Nika 2026-10-01: "keep bottom moving clouds as one of
// the options to be switched from personalize sidebar. clouds and moutain
// should be the default"). The shell mounts the sky with `fadeTo: 'top'`; the
// person's `homeLook.skyLayout` (functions/homeLook.js — the Personalise
// panel's Background group) turns it back into today's bottom band:
// 'bottom' → 'horizon', exactly the placement the sky had before. No stored
// value (or an unregistered reader) → 'top'. Every other `fadeTo` passes
// through untouched. `s` is the layer's state (s.root = the shell root).
export const ambientSkyPlacement = function ambientSkyPlacement(fadeTo, s) {
  if (fadeTo !== 'top') return typeof fadeTo === 'string' && fadeTo ? fadeTo : 'horizon'
  const el = this
  const root = s && s.root ? s.root : s || null
  const look = el && typeof el.call === 'function' ? el.call('homeLookRead', root) : null
  return look && look.skyLayout === 'bottom' ? 'horizon' : 'top'
}

// Does this sky DRIFT? Only a mask that tiles across the width without a seam
// may repeat and slide (files/ambient/SOURCES.md says which): the clouds.
// Everything else — the mountains, the lake, the window — stands still.
export const ambientSkyDrifts = function ambientSkyDrifts(sky) {
  return sky === 'clouds'
}

// Spec §7 probe 2 in one predicate: is the sky mask a url()? Takes a computed
// style or a rule's style (getPropertyValue) or a plain object; the -webkit-
// twin counts too (the framework emits it beside the standard property).
export const ambientSkyPainted = function ambientSkyPainted(style) {
  const read = (camel, kebab) => {
    if (!style || typeof style !== 'object') return ''
    const direct = style[camel]
    if (typeof direct === 'string' && direct) return direct
    return typeof style.getPropertyValue === 'function' ? style.getPropertyValue(kebab) || '' : ''
  }
  const isUrl = (v) => /(^|,\s*)url\(/.test(v)
  if (isUrl(read('maskImage', 'mask-image'))) return { painted: true, via: 'mask-image' }
  if (isUrl(read('webkitMaskImage', '-webkit-mask-image'))) {
    return { painted: true, via: '-webkit-mask-image' }
  }
  return { painted: false, via: null }
}

// Everything the layer paints from → { enabled, sky, reason, url }.
// `s` is the layer's state (s.root = the shell root). Every read goes through
// el.call: `workspaceSettings` (shared — the one accessor for the record, with
// its ready/pending bit), `hasStageOverride` (functions/workspaceSkin.js, the
// record's ground forms), and this file's own helpers; the sky reads the
// server's `resolvedAppearance` beside the record. Reduced transparency is
// read from the element's window (matchMedia); without one (SSR, jsdom) it
// counts as not requested. The skin is NOT read here — see the header.
export const ambientSkyState = function ambientSkyState(s) {
  const el = this
  const root = s && s.root ? s.root : s || null
  const ws = el.call('workspaceSettings', s)
  const ready = !!(ws && ws.ready)
  const settings = ready ? ws.settings || {} : null
  const pending = !!(ws && !ready && ws.pending)
  const record = (ws && ws.workspace) || (root && root.workspace) || null
  const look = record && record.resolvedAppearance ? record.resolvedAppearance : null
  // The record's ground forms come from the shared rule; an unregistered rule
  // answers undefined, and the sky then stays off (fail closed).
  const recordStage = el.call('hasStageOverride', settings)
  if (recordStage === undefined) {
    return { enabled: false, sky: null, reason: 'stage-unknown', url: null }
  }
  const stageOverride =
    !!recordStage || !!el.call('ambientModuleStage', root ? root.workspaceProject : null)
  const ctx = el.context || {}
  const win = ctx.window || (typeof window !== 'undefined' ? window : null)
  const media =
    win && typeof win.matchMedia === 'function'
      ? win.matchMedia('(prefers-reduced-transparency: reduce)')
      : null
  const reduceTransparency = !!(media && media.matches)
  // The person's own sky (functions/homeLook.js) — an unregistered reader
  // answers undefined, and the workspace's sky stands.
  const mine = el.call('homeLookRead', root)
  const pick = el.call('resolveSky', settings, {
    reduceTransparency,
    stageOverride,
    pending,
    resolvedSky: look ? look.sky : null,
    userSky: mine ? mine.sky : null
  })
  if (!pick.enabled) return { enabled: false, sky: null, reason: pick.reason, url: null }
  const url = el.call('ambientSkyUrl', pick.sky)
  if (!url) return { enabled: false, sky: pick.sky, reason: 'asset-missing', url: null }
  return { enabled: true, sky: pick.sky, reason: pick.reason, url }
}

// fadeTo 'grid': the top edge of the layer's `fadeAnchor` node (the home
// grid), found through the DOMQL tree, measured against the layer's own box.
// fadeTo 'top': the CLEAR EDGE — the anchor's top (the shell mounts the
// greeting's summary first, `fadeAnchor: ['HomeTopSummary', …]`) in the home
// scroller's CONTENT, i.e. where it sits on screen at scroll 0: its viewport
// top plus the scroller's offset (the sky scrolls away with the page, so that
// is where the summary always meets it). A hidden anchor (no box) measures
// nothing. Read on render, on window resize and when the summary renders
// (ambientSkySync) — no ResizeObserver.
//
// 'top' takes a LIST of anchors too (the shell mounts the home head's rows in
// order: the summary, the App strip, the boards row, the grid): the FIRST one
// with a box is the clear edge. A home whose head is off — the greeting
// hidden by the person (Customize) — then still stops the band at its first
// row, instead of running the whole 60vh band behind the Dock and the first
// cards (measured on dev Vault 2026-10-03, its greeting then off by the
// app-mode default: no edge, the band to 708.6 px of a 1181 px screen;
// Unreal's edge 149 px).
export const ambientFadeTop = function ambientFadeTop(el) {
  const self = el || this
  if (!self || !self.node || typeof self.getRoot !== 'function') return null
  if (self.fadeTo !== 'grid' && self.fadeTo !== 'top') return null
  const root = self.getRoot()
  const keys = Array.isArray(self.fadeAnchor) ? self.fadeAnchor : [self.fadeAnchor]
  const boxOf = (key) => {
    const anchor = key && root && typeof root.lookdown === 'function' ? root.lookdown(key) : null
    const node = anchor ? anchor.node : null
    if (!node || typeof node.getBoundingClientRect !== 'function') return null
    return node.getBoundingClientRect()
  }
  if (self.fadeTo === 'top') {
    let rect = null
    for (const key of keys) {
      const box = boxOf(key)
      if (box && box.height > 0) {
        rect = box
        break
      }
    }
    if (!rect) return null
    const scroller = typeof root.lookdown === 'function' ? root.lookdown('MainColumn') : null
    const offset = scroller && scroller.node ? Number(scroller.node.scrollTop) || 0 : 0
    const edge = rect.top + offset
    return Number.isFinite(edge) && edge > 0 ? Math.round(edge) : null
  }
  const rect = boxOf(keys[0])
  if (!rect) return null
  const top = rect.top - self.node.getBoundingClientRect().top
  return Number.isFinite(top) && top > 0 ? Math.round(top) : null
}

// Measure and store the fade anchor in the layer's state, only on a change.
// 'top' keeps its last edge while no anchor has a box (the home not mounted,
// not laid out yet), so text never meets a full band.
export const ambientSyncFade = function ambientSyncFade(el, s) {
  const self = el || this
  if (!self || typeof self.call !== 'function') return null
  if (self.fadeTo !== 'grid' && self.fadeTo !== 'top') return null
  const top = self.call('ambientFadeTop', self)
  if (self.fadeTo === 'top' && top == null) return s ? s.fadeTop : null
  if (s && typeof s.update === 'function' && s.fadeTop !== top) s.update({ fadeTop: top })
  return top
}

// Coalesce the layer, grid, summary and resize hooks into one measurement
// after the current DOM updates. The pending frame belongs to this element,
// so removing or replacing it cannot update an old layer's state.
export const ambientScheduleFade = function ambientScheduleFade(el, s) {
  const layer = el || this
  if (!layer || !layer.node || typeof layer.call !== 'function') return null
  if (layer.fadeTo !== 'grid' && layer.fadeTo !== 'top') return null
  const pending = layer.__ambientFadeFrame
  if (pending && pending.node === layer.node) {
    pending.state = s
    return s ? s.fadeTop : null
  }
  if (pending) layer.call('ambientCancelFade', layer)
  const ctx = layer.context || {}
  const win = ctx.window || (typeof window !== 'undefined' ? window : null)
  if (!win || typeof win.requestAnimationFrame !== 'function') {
    return layer.call('ambientSyncFade', layer, s)
  }
  const frame = { node: layer.node, state: s, id: null, win }
  layer.__ambientFadeFrame = frame
  frame.id = win.requestAnimationFrame(() => {
    if (layer.__ambientFadeFrame !== frame) return
    try {
      if (layer.node === frame.node && frame.node.isConnected !== false) {
        layer.call('ambientSyncFade', layer, frame.state)
      }
    } finally {
      if (layer.__ambientFadeFrame === frame) layer.__ambientFadeFrame = null
    }
  })
  return s ? s.fadeTop : null
}

export const ambientCancelFade = function ambientCancelFade(el) {
  const layer = el || this
  const frame = layer && layer.__ambientFadeFrame
  if (!frame) return
  layer.__ambientFadeFrame = null
  if (typeof frame.win.cancelAnimationFrame === 'function') {
    frame.win.cancelAnimationFrame(frame.id)
  }
}

// The anchor's own hook (HomeTopSummary's onRender): schedule the sky's
// clear edge once the summary has a box. → the last edge, or null.
export const ambientSkySync = function ambientSkySync(el) {
  const self = el || this
  if (!self || typeof self.getRoot !== 'function') return null
  const root = self.getRoot()
  const sky = root && typeof root.lookdown === 'function' ? root.lookdown('AmbientBackdrop') : null
  if (!sky || typeof sky.call !== 'function') return null
  return sky.call('ambientScheduleFade', sky, sky.state)
}

// THE SKY LEAVES WITH THE PAGE TOP. Where CSS scroll-driven animations exist,
// the shell sheet slides the top sky up 1:1 with the home scroll (so the
// summary never scrolls into it). Without them (the fallback), the home
// scroller's onScroll (pages/main.js MainColumn) calls this: the sky fades out
// once the home leaves its top, and back at the top. Writes only on a change;
// no work at all where the CSS path runs. → the value set, or null.
export const ambientSkyScrolled = function ambientSkyScrolled(el) {
  const self = el || this
  if (!self || !self.node || typeof self.getRoot !== 'function') return null
  const ctx = self.context || {}
  const win = ctx.window || (typeof window !== 'undefined' ? window : null)
  const css = win ? win.CSS : null
  if (css && typeof css.supports === 'function' && css.supports('animation-timeline: scroll()')) {
    return null
  }
  const root = self.getRoot()
  const sky = root && typeof root.lookdown === 'function' ? root.lookdown('AmbientBackdrop') : null
  const s = sky ? sky.state : null
  if (!s || typeof s.update !== 'function') return null
  const away = (Number(self.node.scrollTop) || 0) > 0
  if (!!s.skyAway !== away) s.update({ skyAway: away })
  return away
}

// THE GROUND (DESIGN-WL-HOME-SKY-TOP-GROUND-1, Nika 2026-10-01: "very end of
// scroll we show some bottom part revealing with smooth scroll in homepage").
// A band of art along the bottom edge (components/AmbientGround.js — the
// loader's mountain range by default) that is NOT on screen while the home
// is scrolled above its end and RISES into view over the last stretch of the
// home scroll. The motion itself is CSS (app.js's injected sheet: a
// scroll-driven animation on the home scroller's named timeline); this file
// carries the rule as a model and the fallback's toggle.

// The ground's own switches → { enabled, sky, reason, url }. The ground is
// part of the ambient sky: every off switch of the sky (pending, the person's
// or the workspace's 'none', reduced transparency, a ground override, a
// missing record) turns it off too — it asks `ambientSkyState`, never a
// second rule. Its art is its own (`groundSky`, default 'mountains') and must
// resolve through the files plane, else nothing paints.
export const ambientGroundState = function ambientGroundState(s, groundSky) {
  const el = this
  const sky = el.call('ambientSkyState', s)
  if (!sky || (!sky.enabled && sky.reason !== 'asset-missing')) {
    return { enabled: false, sky: null, reason: sky ? sky.reason : 'sky-unknown', url: null }
  }
  // The person's 'bottom' layout is the sky as it was before: no ground.
  const root = s && s.root ? s.root : s || null
  const look = el.call('homeLookRead', root)
  if (look && look.skyLayout === 'bottom') {
    return { enabled: false, sky: null, reason: 'layout-bottom', url: null }
  }
  const art = typeof groundSky === 'string' && groundSky ? groundSky : 'mountains'
  const url = el.call('ambientSkyUrl', art)
  if (!url) return { enabled: false, sky: art, reason: 'asset-missing', url: null }
  return { enabled: true, sky: art, reason: sky.reason, url }
}

// THE RULE, as numbers → progress 0..1 of the rise (0 = below the edge, 1 =
// fully up). m: { scrollTop, scrollHeight, clientHeight, stretch, reduced }
// (px; `stretch` = the last stretch of the scroll the rise spans — the
// sheet's 40vh). The CSS twin is `animation-range: max(0px, calc(100% -
// 40vh)) 100%` on the scroller's timeline:
//   * no scroll range (a short home): 0 — the ground belongs to "you reached
//     the end", and a page with no scroll has no end to reach (a scroll
//     timeline with no range is inactive, so the CSS shows the base: hidden);
//   * a range shorter than the stretch: the rise spans the whole range, so
//     the ground is still hidden at the top;
//   * reduced motion: no motion — 0 until the very end, then 1.
export const ambientGroundProgress = function ambientGroundProgress(m) {
  const v = m && typeof m === 'object' ? m : {}
  const num = (x) => (typeof x === 'number' && Number.isFinite(x) ? x : 0)
  const range = Math.max(0, num(v.scrollHeight) - num(v.clientHeight))
  if (range < 1) return 0
  const top = Math.min(range, Math.max(0, num(v.scrollTop)))
  const stretch = Math.max(0, num(v.stretch))
  const start = Math.max(0, range - stretch)
  // within one px of the end counts as the end (fractional scroll offsets)
  if (v.reduced) return top >= range - 1 ? 1 : 0
  if (range - start < 1) return top >= range - 1 ? 1 : 0
  return Math.min(1, Math.max(0, (top - start) / (range - start)))
}

// THE FALLBACK (no CSS scroll-driven animations — Firefox today): the home's
// end sentinel (pages/main.js HomeGroundMark) watches itself with an
// IntersectionObserver rooted on the home scroller, its bottom margin one
// stretch tall, so it fires as the last stretch begins and as it ends. Each
// change reads the scroller's numbers (read-only) through
// `ambientGroundProgress` and toggles the ground's `groundIn` — the sheet then
// runs a transform transition (or, under reduced motion, a plain swap).
// A browser with scroll-driven animations needs none of it: no observer.
// Returns the observer (or null).
export const ambientGroundWatch = function ambientGroundWatch(el) {
  const self = el || this
  if (!self || !self.node || typeof self.call !== 'function') return null
  const ctx = self.context || {}
  const win = ctx.window || (typeof window !== 'undefined' ? window : null)
  if (!win || typeof win.IntersectionObserver !== 'function') return null
  const css = win.CSS
  if (css && typeof css.supports === 'function' && css.supports('animation-timeline: scroll()')) {
    return null
  }
  const scroller = self.parent && self.parent.node ? self.parent.node : null
  if (!scroller) return null
  if (self.scope && self.scope.groundObserver) self.scope.groundObserver.disconnect()
  const media =
    typeof win.matchMedia === 'function' ? win.matchMedia('(prefers-reduced-motion: reduce)') : null
  const sync = () => {
    const doc = win.document
    const root = doc ? doc.documentElement : null
    const reduced =
      !!(media && media.matches) || !!(root && root.getAttribute('data-motion') === 'reduced')
    const p = self.call('ambientGroundProgress', {
      scrollTop: scroller.scrollTop,
      scrollHeight: scroller.scrollHeight,
      clientHeight: scroller.clientHeight,
      stretch: scroller.clientHeight * 0.4,
      reduced
    })
    // reduced motion: shown only at the very end; else from the stretch start
    self.call('ambientGroundSet', self, reduced ? p >= 1 : p > 0)
  }
  const io = new win.IntersectionObserver(sync, {
    root: scroller,
    rootMargin: '0px 0px 40% 0px',
    threshold: [0, 1]
  })
  io.observe(self.node)
  if (self.scope) self.scope.groundObserver = io
  return io
}

// Stop watching (the sentinel's onRemove) and lower the ground.
export const ambientGroundUnwatch = function ambientGroundUnwatch(el) {
  const self = el || this
  const io = self && self.scope ? self.scope.groundObserver : null
  if (io) io.disconnect()
  if (self && self.scope) self.scope.groundObserver = null
  if (self && typeof self.call === 'function') self.call('ambientGroundSet', self, false)
  return null
}

// Set the ground's `groundIn`, found through the DOMQL tree; writes only on a
// change. → the value set, or null when there is no ground.
export const ambientGroundSet = function ambientGroundSet(el, on) {
  const self = el || this
  if (!self || typeof self.getRoot !== 'function') return null
  const root = self.getRoot()
  const ground = root && typeof root.lookdown === 'function' ? root.lookdown('AmbientGround') : null
  const s = ground ? ground.state : null
  if (!s || typeof s.update !== 'function') return null
  const next = !!on
  if (s.groundIn !== next) s.update({ groundIn: next })
  return next
}
